import * as THREE from "three";

/**
 * Falling precipitation — rain or snow — as one instanced draw call that
 * follows the camera. The rain is a BOX AROUND THE EYE: six hundred streaks in
 * a 12 m cube centred on the camera, each falling and wrapping back to the top
 * with its x/z untouched. Nothing is spawned or destroyed, so the cost is one
 * matrix write per streak per frame. Each drop owns a fixed world-space x/z and
 * what is drawn is its nearest periodic image, so the drops stay still in the
 * world while the box glides with the player.
 *
 * Rain and snow differ only in the `PARAMS` table — count, size, speed, colour,
 * and snow's slow horizontal drift, which is what says "light enough to be
 * pushed by the air". It does not collide (falls through roofs), is not lit or
 * fogged and writes no depth: a screen effect living in world space.
 */

/** Which of the two the field is drawing. */
export type PrecipKind = "rain" | "snow";

/**
 * Streaks in the air at once — one every two metres of volume reads as steady
 * rain; 600 × 16 floats is a 38 KB upload a frame. Snow gets half again as
 * many: a flake is a speck, and it spends seven times as long in shot.
 */
const DROP_COUNT = 600;
const FLAKE_COUNT = 900;

/** Half-extent of the box the rain lives in, metres. */
const BOX_HALF = 6;
/** Full box edge — the period every drop's world position is wrapped by. */
const BOX_SIZE = BOX_HALF * 2;

/**
 * Fall speed, m/s. Real rain terminates around 9 and looks it — slower reads as
 * snow, faster becomes a static texture of lines. Snow at 1.3 is slow enough to
 * follow ONE flake down past a window.
 */
const FALL_SPEED = 9;
const FLAKE_FALL_SPEED = 1.3;

/**
 * One streak: a 5 mm column, 35 cm long — a hairline against a wall three
 * metres away (1.5 cm filled the screen with grey bars).
 */
const DROP_W = 0.005;
const DROP_H = 0.35;

/**
 * One flake: a 2.5 cm cube. Cubic rather than long — a streak says falling
 * fast, a dot says drifting.
 */
const FLAKE_SIZE = 0.025;

/**
 * Horizontal drift, a per-flake sine wander in m/s. Two frequencies and
 * amplitudes so a flake traces a lopsided ellipse, with its own phase so the
 * field wanders as specks rather than a sheet. A quarter of the fall speed.
 */
const DRIFT_X_AMP = 0.35;
const DRIFT_X_RATE = 0.9;
const DRIFT_Z_AMP = 0.25;
const DRIFT_Z_RATE = 0.7;

/**
 * Drops nearer the eye than this (horizontally) are not drawn — a streak 20 cm
 * from the lens is a smear across the screen.
 */
const NEAR_CULL = 0.5;
const NEAR_CULL_SQ = NEAR_CULL * NEAR_CULL;

/**
 * Additive grey: rain is BRIGHTER than what is behind it, which is why one
 * material works over a black night and a pale fog bank.
 */
const DROP_COLOR = 0x9fb0c4;
const DROP_OPACITY = 0.22;

/**
 * Snow is nearly white and three times as present — a flake reflects the sky
 * rather than bending it, and anything dimmer disappears against snow's pale
 * sky.
 */
const FLAKE_COLOR = 0xe6ecf2;
const FLAKE_OPACITY = 0.7;

/** The numbers that separate the two kinds. Everything else in this file is
 *  shared. */
interface PrecipParams {
  count: number;
  geometry(): THREE.BufferGeometry;
  fallSpeed: number;
  color: number;
  opacity: number;
  /** Snow only: the sideways wander that makes a slow fall read as a flake. */
  drift: boolean;
}

const PARAMS: Record<PrecipKind, PrecipParams> = {
  rain: {
    count: DROP_COUNT,
    // A COLUMN rather than a quad: a quad faces one way and is invisible
    // edge-on,
    // and billboarding 600 instances costs more per frame forever than 7 000
    // triangles once. A square column needs no orientation — pure translations.
    geometry: () => new THREE.BoxGeometry(DROP_W, DROP_H, DROP_W),
    fallSpeed: FALL_SPEED,
    color: DROP_COLOR,
    opacity: DROP_OPACITY,
    drift: false,
  },
  snow: {
    count: FLAKE_COUNT,
    // A cube gets the same view-independence for free, and at 2.5 cm it is
    // below the size where anybody could tell a cube from a crystal.
    geometry: () => new THREE.BoxGeometry(FLAKE_SIZE, FLAKE_SIZE, FLAKE_SIZE),
    fallSpeed: FLAKE_FALL_SPEED,
    color: FLAKE_COLOR,
    opacity: FLAKE_OPACITY,
    drift: true,
  },
};

export interface RainField {
  /** Add this to the scene. Removed and disposed by `dispose`. */
  mesh: THREE.InstancedMesh;
  /** One frame. `dt` is seconds; the camera position is the box's centre. */
  update(cameraPos: THREE.Vector3, dt: number): void;
  dispose(): void;
}

/**
 * Builds the field. Created only for a raid whose weather is rain or snow and
 * disposed when it ends — a clear raid pays nothing.
 */
export function createPrecipitation(kind: PrecipKind): RainField {
  const params = PARAMS[kind];
  const count = params.count;

  const geo = params.geometry();
  const mat = new THREE.MeshBasicMaterial({
    color: params.color,
    transparent: true,
    opacity: params.opacity,
    blending: THREE.AdditiveBlending,
    // No depth writes (never occlude each other, no sorting) and no fog (within
    // 6 m of the eye — fogging would fade the near ones).
    depthWrite: false,
    fog: false,
  });

  const mesh = new THREE.InstancedMesh(geo, mat, count);
  mesh.name = kind;
  // Re-centred on the camera every frame, so the construction-time bounding
  // sphere is meaningless.
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

  // Per-instance WORLD positions, each within one period [0, BOX_SIZE): only
  // the
  // residue matters. `Math.random` on purpose — scenery, not simulation.
  const ox = new Float32Array(count);
  const oy = new Float32Array(count);
  const oz = new Float32Array(count);
  // A per-drop speed spread, so the field does not read as one rigid sheet
  // sliding downwards.
  const speed = new Float32Array(count);
  // Snow only (zeros for rain): each flake's own phase in its wander, or every
  // flake swings together as a curtain.
  const phase = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    ox[i] = Math.random() * BOX_SIZE;
    oy[i] = Math.random() * BOX_SIZE;
    oz[i] = Math.random() * BOX_SIZE;
    speed[i] = params.fallSpeed * (0.85 + Math.random() * 0.3);
    if (params.drift) phase[i] = Math.random() * Math.PI * 2;
  }

  // The drift's clock, accumulated here so a field created mid-raid starts its
  // wander at zero.
  let t = 0;

  // Scratch. `update` runs every frame and MUST NOT allocate.
  const m = new THREE.Matrix4();

  return {
    mesh,
    update(cameraPos: THREE.Vector3, dt: number): void {
      // Clamped: a tab that was in the background for a minute must not
      // teleport the whole field, it must simply carry on raining.
      const step = Math.min(dt, 0.1);
      t += step;
      const cx = cameraPos.x;
      const cy = cameraPos.y;
      const cz = cameraPos.z;

      for (let i = 0; i < count; i++) {
        let y = (oy[i] ?? 0) - (speed[i] ?? params.fallSpeed) * step;
        // Wrapped rather than reset: same x and z, back at the ceiling.
        if (y < 0) y += BOX_SIZE;
        oy[i] = y;

        // Snow wanders sideways, applied to the world residue and wrapped back
        // into
        // the period — a flake blown out one side comes back in the other.
        if (params.drift) {
          const ph = phase[i] ?? 0;
          let x = (ox[i] ?? 0) + Math.sin(t * DRIFT_X_RATE + ph) * DRIFT_X_AMP * step;
          let z = (oz[i] ?? 0) + Math.cos(t * DRIFT_Z_RATE + ph) * DRIFT_Z_AMP * step;
          if (x < 0) x += BOX_SIZE;
          else if (x >= BOX_SIZE) x -= BOX_SIZE;
          if (z < 0) z += BOX_SIZE;
          else if (z >= BOX_SIZE) z -= BOX_SIZE;
          ox[i] = x;
          oz[i] = z;
        }

        // The image of this drop nearest the camera: offset from the eye,
        // reduced
        // into [-BOX_HALF, BOX_HALF) on every axis.
        let dx = (ox[i] ?? 0) - cx;
        let dy = y - cy;
        let dz = (oz[i] ?? 0) - cz;
        dx -= Math.round(dx / BOX_SIZE) * BOX_SIZE;
        dy -= Math.round(dy / BOX_SIZE) * BOX_SIZE;
        dz -= Math.round(dz / BOX_SIZE) * BOX_SIZE;

        m.makeTranslation(cx + dx, cy + dy, cz + dz);
        if (dx * dx + dz * dz < NEAR_CULL_SQ) {
          // Collapse the column to nothing rather than moving it: the drop keeps
          // its world position and reappears the moment the player steps away.
          const e = m.elements;
          e[0] = 0;
          e[5] = 0;
          e[10] = 0;
        }
        mesh.setMatrixAt(i, m);
      }
      mesh.instanceMatrix.needsUpdate = true;
    },
    dispose(): void {
      mesh.removeFromParent();
      mesh.dispose();
      geo.dispose();
      mat.dispose();
    },
  };
}

/** The rain, by its old name. Kept for callers that only ever want rain. */
export function createRain(): RainField {
  return createPrecipitation("rain");
}
